/**
 * ADR-0229 — one proposal on the Agent registry board.
 *
 * Candidates get the full approve form (category, id, display name,
 * company, docs URL, pattern — all re-validated server-side). Spoof
 * clusters and dormant agents only get Dismiss / Snooze.
 *
 * ADR-0230: the form is pre-filled from the AI classification, so a clean
 * candidate gets a one-click "Approve"; "Edit…" opens the form.
 */

import { useState } from 'react';

import type { AdminAgentRegistryProposal, AdminAgentRegistryWarning } from '@sill/types/api';
import { AI_KIND_EXPLANATION, AI_KIND_LABEL } from '@sill/types';

import { useAdminApi } from '../../api/api-context.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import {
  CATEGORY_OPTIONS,
  COMPANY_HELP,
  categoryLabel,
  decisionError,
  draftFromProposal,
  draftProblems,
  shortKey,
  toApproveBody,
  warningsFromError,
  type ApproveDraft,
} from './registry-helpers.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface ProposalCardProps {
  readonly proposal: AdminAgentRegistryProposal;
  readonly onDecided: () => void;
}

export function ProposalCard({ proposal: p, onDecided }: ProposalCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [mode, setMode] = useState<'idle' | 'approve'>('idle');
  const [draft, setDraftState] = useState<ApproveDraft>(() => draftFromProposal(p));
  // ADR-0230 amendment 2026-10-07 — warnings the server wants confirmed.
  const [warnings, setWarnings] = useState<AdminAgentRegistryWarning[] | null>(null);
  const setDraft = (d: ApproveDraft): void => {
    setDraftState(d);
    setWarnings(null);
  };
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ev = p.evidence;
  const problems = p.kind === 'candidate' ? draftProblems(draft, p.sample_user_agent) : [];
  const ai = p.ai_classification ?? null;

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDecided();
    } catch (err) {
      setError(decisionError(err));
    } finally {
      setBusy(false);
    }
  };

  const approve = async (acknowledged?: readonly string[]): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.approveAgentProposal(p.proposal_id, toApproveBody(draft, acknowledged));
      setWarnings(null);
      onDecided();
    } catch (err) {
      const w = warningsFromError(err);
      if (w !== null && w.length > 0) setWarnings(w);
      else setError(decisionError(err));
    } finally {
      setBusy(false);
    }
  };

  const label = { fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' as const, color: C.muted };
  const input = {
    fontFamily: sansFont,
    fontSize: 12.5,
    padding: '6px 8px',
    background: C.surface2,
    color: C.text,
    border: `1px solid ${C.border}`,
    borderRadius: 3,
    width: '100%',
  };
  const btn = (primary: boolean, danger = false) => ({
    fontFamily: sansFont,
    fontSize: 12.5,
    fontWeight: primary ? 600 : 400,
    padding: '6px 12px',
    background: primary ? C.lime : 'transparent',
    color: primary ? C.ink : danger ? C.coralFg : C.body,
    border: `1px solid ${primary ? C.lime : C.border}`,
    borderRadius: 3,
    cursor: busy ? 'default' : 'pointer',
    opacity: busy ? 0.6 : 1,
  });

  return (
    <Card style={{ padding: 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 600, color: C.text }}>
          {p.display_name ?? p.candidate_key}
        </span>
        {p.kind === 'candidate' ? <Pill tone="sky">{categoryLabel(p.suggested_category)}</Pill> : null}
        {p.suggested_category === 'hostile' ? <Pill tone="coral">flagged hostile</Pill> : null}
        {p.auto_eligible ? <Pill tone="lime">auto-eligible</Pill> : null}
        {(ev.signature_verified_hits ?? 0) > 0 ? <Pill tone="mint">signed</Pill> : null}
        {p.status === 'snoozed' && p.snoozed_until !== null ? (
          <Pill tone="neutral">snoozed until {formatIsoCompact(p.snoozed_until)}</Pill>
        ) : null}
        <span style={{ marginLeft: 'auto', fontFamily: monoFont, fontSize: 11, color: C.muted }}>
          {p.kind === 'dormant'
            ? `last seen ${p.last_seen_at !== null ? formatIsoCompact(p.last_seen_at) : 'never'}`
            : `${p.hits.toLocaleString()} hits · ${p.site_count} site${p.site_count === 1 ? '' : 's'} · ${p.window_days}d`}
        </span>
      </div>

      {p.sample_user_agent !== null ? (
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.body, marginTop: 8, wordBreak: 'break-all' }}>
          {p.sample_user_agent}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 8, fontFamily: sansFont, fontSize: 12, color: C.muted }}>
        {p.first_seen_at !== null ? <span>first {formatIsoCompact(p.first_seen_at)}</span> : null}
        {p.kind !== 'dormant' && p.last_seen_at !== null ? <span>last {formatIsoCompact(p.last_seen_at)}</span> : null}
        {p.kind === 'candidate' ? (
          <span>
            distinct verified signatures: {ev.signature_verified_hits ?? 0}
            {ev.distinct_accounts !== undefined ? ` · ${ev.distinct_accounts} account${ev.distinct_accounts === 1 ? '' : 's'}` : ''}
            {(ev.signer_hosts ?? []).length > 0 ? ` (${(ev.signer_hosts ?? []).join(', ')})` : ''}
          </span>
        ) : null}
        {p.kind === 'candidate' ? (
          <span title="Reported by the merchant's Cloudflare install. Advisory: the beacon is unauthenticated.">
            Cloudflare-reported verified: {ev.cf_reported_verified_hits ?? 0}
          </span>
        ) : null}
        {(ev.impersonation_hits ?? 0) > 0 ? <span style={{ color: C.coralFg }}>impersonation hits: {ev.impersonation_hits}</span> : null}
        {p.kind === 'spoof_cluster' ? (
          <span>
            {ev.cluster_minutes ?? 0} minute(s) · {ev.distinct_ip_count ?? 0} distinct IPs · {ev.distinct_user_agents ?? 0} UA strings
          </span>
        ) : null}
        {p.kind === 'dormant' && ev.organization !== undefined ? <span>{ev.organization}</span> : null}
      </div>

      {p.kind === 'spoof_cluster' && (ev.claimed_tokens ?? []).length > 0 ? (
        <div style={{ marginTop: 6, fontFamily: monoFont, fontSize: 11, color: C.body }}>
          claimed: {(ev.claimed_tokens ?? []).join(', ')}
        </div>
      ) : null}

      {ev.ai_summary !== undefined && ev.ai_summary !== null && ev.ai_summary.who !== undefined ? (
        <div style={{ marginTop: 6, fontFamily: sansFont, fontSize: 12.5, color: C.body }}>
          <span style={label}>AI summary </span> {ev.ai_summary.who}
          {ev.ai_summary.verdict !== undefined ? ` · ${ev.ai_summary.verdict}` : ''}
        </div>
      ) : null}

      {p.kind === 'candidate' && ai !== null ? (
        <div data-testid="ai-classification" style={{ marginTop: 6, fontFamily: sansFont, fontSize: 12.5, color: C.body }}>
          <span style={label}>AI says </span>
          <strong style={{ color: C.text }}>{AI_KIND_LABEL[ai.kind]}</strong>
          {ai.company !== null ? ` · ${ai.company}` : ''}
          {` · confidence ${ai.confidence.toFixed(2)}`}
          {ai.reason.length > 0 ? ` — ${ai.reason}` : ''}
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{AI_KIND_EXPLANATION[ai.kind]}</div>
        </div>
      ) : p.kind === 'candidate' ? (
        <div style={{ marginTop: 6, fontFamily: sansFont, fontSize: 11.5, color: C.muted }}>AI classification pending (runs every few minutes).</div>
      ) : null}

      {p.kind === 'candidate' && (ev.distinct_days !== undefined || ev.distinct_ip_blocks !== undefined) ? (
        <div style={{ marginTop: 4, fontFamily: sansFont, fontSize: 11.5, color: C.muted }}>
          seen on {ev.distinct_days ?? 0} day(s) · {ev.distinct_ip_blocks ?? 0} address block(s) · {ev.established_accounts ?? 0} established account(s)
          {ev.in_spoof_cluster === true ? ' · claimed in a spoof cluster' : ''}
        </div>
      ) : null}

      {p.kind === 'candidate' && ev.auto_reason !== undefined ? (
        <div style={{ marginTop: 4, fontFamily: sansFont, fontSize: 11.5, color: C.muted }}>Automatic mode: {ev.auto_reason}</div>
      ) : null}

      {(ev.observed_keys ?? []).length > 0 ? (
        <div style={{ marginTop: 4, fontFamily: monoFont, fontSize: 10.5, color: C.muted }}>
          observed: {(ev.observed_keys ?? []).slice(0, 6).map(shortKey).join(' ')}
        </div>
      ) : null}

      {mode === 'approve' ? (
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={label}>Category</span>
            <select
              aria-label="Category"
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value as ApproveDraft['category'] })}
              style={input}
            >
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={label}>Agent id</span>
            <input aria-label="Agent id" value={draft.agent_id} onChange={(e) => setDraft({ ...draft, agent_id: e.target.value })} style={input} />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={label}>Display name</span>
            <input aria-label="Display name" value={draft.display_name} onChange={(e) => setDraft({ ...draft, display_name: e.target.value })} style={input} />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={label}>Company</span>
            <input aria-label="Company" value={draft.operator} onChange={(e) => setDraft({ ...draft, operator: e.target.value })} style={input} />
            <span style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}>{COMPANY_HELP}</span>
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={label}>Docs URL (first-party)</span>
            <input aria-label="Docs URL" value={draft.docs_url} onChange={(e) => setDraft({ ...draft, docs_url: e.target.value })} style={input} placeholder="https://" />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={label}>UA pattern</span>
            <input aria-label="UA pattern" value={draft.pattern} onChange={(e) => setDraft({ ...draft, pattern: e.target.value })} style={{ ...input, fontFamily: monoFont }} />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={label}>Published IP list (optional)</span>
            <input aria-label="IP list URL" value={draft.ip_list_url} onChange={(e) => setDraft({ ...draft, ip_list_url: e.target.value })} style={input} placeholder="https:// on the company's own domain" />
            <select
              aria-label="IP list format"
              value={draft.ip_list_format}
              onChange={(e) => setDraft({ ...draft, ip_list_format: e.target.value as ApproveDraft['ip_list_format'] })}
              style={input}
            >
              <option value="google_json">JSON (prefixes list)</option>
              <option value="plain_list">Plain text (one range per line)</option>
            </select>
          </label>
          <div style={{ gridColumn: '1 / -1', fontFamily: sansFont, fontSize: 12, color: C.muted }}>
            {draft.category === 'hostile'
              ? 'Hostile approvals are recorded at origin only (flagged on the board); they are never written to the edge registry.'
              : 'Approval writes this identity to the edge registry. A user-agent match is shown to merchants as a claim, not as verified.'}
          </div>
          {problems.length > 0 ? (
            <ul style={{ gridColumn: '1 / -1', margin: 0, paddingLeft: 18, fontFamily: sansFont, fontSize: 12, color: C.coralFg }}>
              {problems.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {warnings !== null ? (
        <div
          data-testid="approval-warnings"
          style={{ marginTop: 10, padding: 10, border: `1px solid ${C.amber}`, borderRadius: 3, fontFamily: sansFont, fontSize: 12.5, color: C.body }}
        >
          <div style={{ ...label, color: C.amberFg, marginBottom: 6 }}>Check before approving</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {warnings.map((w) => (
              <li key={w.code}>{w.message}</li>
            ))}
          </ul>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" style={btn(true)} disabled={busy} onClick={() => void approve(warnings.map((w) => w.code))}>
              Approve anyway
            </button>
            <button type="button" style={btn(false)} disabled={busy} onClick={() => setWarnings(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {error !== null ? (
        <div role="alert" style={{ marginTop: 8, fontFamily: sansFont, fontSize: 12.5, color: C.coralFg }}>
          {error}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {p.kind === 'candidate' && mode === 'idle' ? (
          <>
            <button
              type="button"
              style={btn(true)}
              disabled={busy || problems.length > 0}
              title={problems.length > 0 ? 'Some fields need a person — use Edit.' : 'Approve with the pre-filled details.'}
              onClick={() => void approve()}
            >
              {draft.category === 'hostile' ? 'Confirm hostile' : 'Approve'}
            </button>
            <button type="button" style={btn(false)} disabled={busy} onClick={() => setMode('approve')}>
              Edit…
            </button>
          </>
        ) : null}
        {mode === 'approve' ? (
          <>
            <button
              type="button"
              style={btn(true)}
              disabled={busy || problems.length > 0}
              onClick={() => void approve()}
            >
              Confirm approval
            </button>
            <button type="button" style={btn(false)} disabled={busy} onClick={() => setMode('idle')}>
              Cancel
            </button>
          </>
        ) : null}
        {mode === 'idle' ? (
          <>
            <input
              aria-label="Reject reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={p.kind === 'candidate' ? 'Reject reason…' : 'Dismiss note…'}
              style={{ ...input, width: 200 }}
            />
            <button
              type="button"
              style={btn(false, true)}
              disabled={busy || reason.trim().length === 0}
              onClick={() => void run(() => api.rejectAgentProposal(p.proposal_id, reason.trim()))}
            >
              {p.kind === 'candidate' ? 'Reject' : 'Dismiss'}
            </button>
            {([7, 30, 90] as const).map((d) => (
              <button
                key={d}
                type="button"
                style={btn(false)}
                disabled={busy}
                onClick={() => void run(() => api.snoozeAgentProposal(p.proposal_id, d))}
              >
                Snooze {d}d
              </button>
            ))}
          </>
        ) : null}
      </div>
    </Card>
  );
}
